import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styles from './ProductManagement.module.css';
import ProductCard from '../ProductManagement/ProductCard';
import masaLogo from '../Assets/MasaLogo.png';
import { motion, AnimatePresence } from 'framer-motion';
import { getProducts, addProductQuantity, removeProductQuantity} from '../api/productApi';
import { createRequest } from '../api/requestApi';

const ProductManagement = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const action = location.state?.action || 'add';

  const [showModal, setShowModal] = useState(false);
  const [productSelections, setProductSelections] = useState({});
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [products, setProducts] = useState([]);
  const [successMessage, setSuccessMessage] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);

  const token = useMemo(() => localStorage.getItem('authToken'), []);
  const userInfo = useMemo(() => {
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return { userType: payload.userType, requesterUsername: payload.username };
      } catch (error) {
        console.error('Error parsing token payload:', error);
        return { userType: null, requesterUsername: null };
      }
    }
    return { userType: null, requesterUsername: null };
  }, [token]);

  useEffect(() => {
    getProducts()
      .then((response) => setProducts(response.data))
      .catch((error) => {
        console.error('Error fetching products:', error);
        setErrorMessage('Failed to fetch products. Please try again.');
      });
  }, []);

  const handleConfirmClick = () => {
    const selected = Object.values(productSelections).filter(
      (selection) => selection.sortName && selection.color && selection.quantity > 0
    );
    if (selected.length === 0) {
      setErrorMessage('Please select at least one product with sort, color, and quantity.');
      return;
    }
    setSelectedProducts(selected);
    setShowModal(true);
  };

  const handleModalConfirm = async () => {
    try {
      for (const product of selectedProducts) {
        const { productId, sortName, color, quantity } = product;
        const prodDetails = products.find((p) => p._id === productId);
        if (!prodDetails) {
          throw new Error('Product not found for the selected item.');
        }

        if (userInfo.userType === 'manager') {
          // managers can directly add or remove products
          const payload = {
            sortName,
            color,
            quantity
          };
          if (action === 'add') {
            await addProductQuantity(prodDetails.SKU, payload);
          } else if (action === 'remove') {
            await removeProductQuantity(prodDetails.SKU, payload);
          }
        }  else if (userInfo.userType === 'worker') {
          await createRequest({
            requestId: `REQ-${Date.now()}`,
            productName: prodDetails.name,
            productSKU: prodDetails.SKU,
            requesterUsername: userInfo.requesterUsername || 'unknown',
            state: 'Null',
            sortName,
            color,
            quantity,
            action
          });
        }
      }
      setSuccessMessage(
        userInfo.userType === 'manager'
          ? `Products successfully ${action === 'add' ? 'added' : 'removed'}!`
          : 'Your request has been submitted for manager approval.'
      );
    } catch (error) {
      console.error(`Error processing products for ${action}:`, error);
      setErrorMessage(
        userInfo.userType === 'manager'
          ? `Failed to ${action} products. Please try again.`
          : 'Failed to submit your request. Please try again.'
      );
    }
    setShowModal(false);
  };

  const handleModalCancel = () => {
    setShowModal(false);
  };

  const handleProductSelectionChange = (productId, selection) => {
    setProductSelections((prevSelections) => {
      const currentSelection = prevSelections[productId];
      if (
        currentSelection &&
        currentSelection.sortName === selection.sortName &&
        currentSelection.color === selection.color &&
        currentSelection.quantity === selection.quantity
      ) {
        return prevSelections;
      }
      return { ...prevSelections, [productId]: selection };
    });
  };

  const handleCloseSuccessModal = () => {
    setSuccessMessage(null);
    navigate(-1);
  };

  const handleCloseErrorModal = () => {
    setErrorMessage(null);
  };

  return (
    <main className={styles.productManagement}>
      <header className={styles.header}>
        <div className={styles.headerWrapper}>
          <img src={masaLogo} alt="Logo" className={styles.headerImage} />
          <h1 className={styles.title}>Product Management - {action === 'add' ? 'Add' : 'Remove'}</h1>
        </div>
      </header>

      <section className={styles.productList}>
        {products.map((product) => (
          <ProductCard
            key={product._id}
            productId={product._id}
            image={product.image}
            name={product.name}
            size={product.size}
            sku={product.SKU}
            sorts={product.sorts || []}
            onSelectionChange={(selection) => handleProductSelectionChange(product._id, selection)}
            action={action}
          />
        ))}
      </section>

      <footer className={styles.footer}>
        <button className={styles.confirmButton} onClick={handleConfirmClick}>Confirm</button>
      </footer>

      <AnimatePresence>
        {showModal && (
          <motion.div
            className={styles.modalBackdrop}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className={styles.modalContent}
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.8 }}
            >
              <h2>Receipt</h2>
              <ul className={styles.receiptList}>
                {selectedProducts.map((product, index) => (
                  <li key={index} className={styles.receiptItem}>
                    <strong>{product.name}</strong> - Sort: {product.sortName}, Color: {product.color}, Quantity: {product.quantity}
                  </li>
                ))}
              </ul>
              <p>Are you sure you want to proceed?</p>
              <button onClick={handleModalConfirm} className={styles.modalConfirmButton}>Yes, Confirm</button>
              <button onClick={handleModalCancel} className={styles.modalCancelButton}>Cancel</button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {successMessage && (
        <div className={styles.modalBackdrop}>
          <div className={styles.modalContent}>
            <h2>Success</h2>
            <p>{successMessage}</p>
            <button onClick={handleCloseSuccessModal} className={styles.modalCloseButton}>Close</button>
          </div>
        </div>
      )}

      {errorMessage && (
        <div className={styles.modalBackdrop}>
          <div className={styles.modalContent}>
            <h2>Error</h2>
            <p>{errorMessage}</p>
            <button onClick={handleCloseErrorModal} className={styles.modalCloseButton}>Close</button>
          </div>
        </div>
      )}
    </main>
  );
};

export default ProductManagement;
